import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { WashingMachine, LogOut, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { useAdminData } from "@/lib/useAdminData";
import { OrdersTable } from "@/components/admin/OrdersTable";
import { StatsCards, OrdersFilters, ReviewsTab, MessagesTab } from "@/components/admin/AdminPanels";

const matchesSearch = (order, q) =>
  [order.order_number, order.customer.name, order.customer.phone, order.customer.email, order.customer.address]
    .some((v) => (v || "").toLowerCase().includes(q));

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { orders, stats, reviews, messages, loading, changeStatus, deleteMessage, deleteReview } = useAdminData(user);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");

  const doLogout = async () => {
    await logout();
    navigate("/admin");
  };

  const q = search.trim().toLowerCase();
  const filteredOrders = orders.filter(
    (o) => (statusFilter === "todos" || o.status === statusFilter) && (!q || matchesSearch(o, q))
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="bg-cyan-500 text-white p-1.5 rounded-lg">
              <WashingMachine className="h-5 w-5" />
            </span>
            <span className="font-heading font-bold text-slate-900">Lavandería Ozono · Admin</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500 hidden sm:block">{user?.email}</span>
            <Button variant="outline" size="sm" onClick={doLogout} data-testid="admin-logout-btn" className="rounded-full">
              <LogOut className="h-4 w-4 mr-2" /> Salir
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-2xl font-heading font-bold text-slate-900 mb-6">Gestión de Pedidos</h1>
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 text-cyan-500 animate-spin" />
          </div>
        ) : (
          <>
            {stats && <StatsCards stats={stats} />}
            <Tabs defaultValue="pedidos">
              <TabsList className="mb-6">
                <TabsTrigger value="pedidos" data-testid="tab-pedidos">Pedidos ({orders.length})</TabsTrigger>
                <TabsTrigger value="resenas" data-testid="tab-resenas">Reseñas ({reviews.length})</TabsTrigger>
                <TabsTrigger value="mensajes" data-testid="tab-mensajes">Mensajes ({messages.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="pedidos">
                <OrdersFilters search={search} setSearch={setSearch} statusFilter={statusFilter} setStatusFilter={setStatusFilter} />
                {(q || statusFilter !== "todos") && (
                  <p className="text-xs text-slate-500 mb-3" data-testid="orders-filter-count">
                    Mostrando {filteredOrders.length} de {orders.length} pedidos
                  </p>
                )}
                <OrdersTable orders={filteredOrders} onStatusChange={changeStatus} />
                <p className="mt-4 text-xs text-slate-500">
                  Al cambiar un pedido a «Listo» o «Entregado», el cliente recibe un aviso automático por email.
                </p>
              </TabsContent>
              <TabsContent value="resenas">
                <ReviewsTab reviews={reviews} onDelete={deleteReview} />
              </TabsContent>
              <TabsContent value="mensajes">
                <MessagesTab messages={messages} onDelete={deleteMessage} />
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>
    </div>
  );
}
